import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Switch, Button, Card, Space, message, Tooltip, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { ReloadOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Option } = Select;
const { TextArea } = Input;

// Formül yardımcı metni
const formulaHelpText = (
  <div>
    <p>Formül yazarken aşağıdaki fonksiyonları kullanabilirsiniz:</p>
    <ul>
      <li><code>sum(metrikAdı)</code> - Günlük metriğin aylık toplamını hesaplar. Örneğin: Aylık toplam satış miktarı</li>
      <li><code>avg(metrikAdı)</code> - Günlük metriğin aylık ortalamasını hesaplar. Örneğin: Günlük ortalama satış miktarı</li>
      <li><code>avgRange(metrikAdı, başlangıçGünü, bitişGünü)</code> - Günlük metriğin belirli gün aralığındaki ortalamasını hesaplar (1'den başlayarak). Örneğin: Ayın ilk 15 günündeki ortalama satış</li>
      <li><code>lastDays(metrikAdı, günSayısı)</code> - Günlük metriğin son X gününün ortalamasını hesaplar. Örneğin: Son 7 günün ortalama satışı</li>
      <li><code>divByDaysInMonth(metrikAdı)</code> - Metrik değerini ayın toplam gün sayısına böler. Örneğin: Aylık hedefin günlük dağılımı</li>
      <li><code>monthlyAvg(metrikAdı, aySayısı)</code> - Bir aylık metriğin son X ay ortalamasını hesaplar. Örneğin: Son 3 aylık ciro ortalaması</li>
      <li><code>monthlyChange(metrikAdı)</code> - Bir aylık metriğin önceki aya göre değişim yüzdesini hesaplar. Örneğin: Cironun geçen aya göre değişimi (%)</li>
    </ul>
    <p>Özel Değişkenler:</p>
    <ul>
      <li><code>CURRENT_DAY</code> - Ayın kaçıncı günü olduğunu verir (1-31 arası)</li>
      <li><code>REMAINING_DAYS</code> - Ayın kalan gün sayısını verir</li>
      <li><code>TOTAL_DAYS</code> - Ayın toplam gün sayısını verir</li>
    </ul>
    <p>Formül Örnekleri:</p>
    <ul>
      <li><code>sum(ciro) / sum(ziyaretçi)</code> - Ziyaretçi başına düşen ciro</li>
      <li><code>avgRange(ciro, 1, 7)</code> - Ayın ilk haftasının ortalama cirosu</li>
      <li><code>lastDays(ciro, 7)</code> - Son 7 günün ortalama cirosu</li>
      <li><code>divByDaysInMonth(sum(ciro))</code> - Günlük ortalama ciro hedefi</li>
      <li><code>sum(ciro) / CURRENT_DAY</code> - Bugüne kadarki günlük ortalama ciro</li>
      <li><code>(sum(ciro) / CURRENT_DAY) * TOTAL_DAYS</code> - Mevcut performansa göre ay sonu tahmini ciro</li>
      <li><code>monthlyAvg(TAHMINI_CIRO, 3)</code> - Son 3 aylık tahmini ciro ortalaması</li>
      <li><code>monthlyChange(GUNCEL_CIRO)</code> - Güncel cironun önceki aya göre değişim yüzdesi (negatif değer azalışı, pozitif değer artışı gösterir)</li>
      <li><code>monthlyAvg(TAHMINI_CIRO, 6) + (monthlyAvg(TAHMINI_CIRO, 6) * 0.1)</code> - Son 6 aylık tahmini ciro ortalamasının %10 fazlası (gelecek hedefi için)</li>
    </ul>
    <p>Not: Formüllerde matematiksel işlemler (+, -, *, /) kullanabilirsiniz. Metrik isimleri büyük/küçük harfe duyarlı değildir.</p>
  </div>
);

const MonthlyMetricForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [brands, setBrands] = useState([]);
    const [allDailyMetrics, setAllDailyMetrics] = useState([]);
    const [allMonthlyMetrics, setAllMonthlyMetrics] = useState([]);
    const [loadingAllMetrics, setLoadingAllMetrics] = useState(false);
    const [applyToAll, setApplyToAll] = useState(false);
    const [massFormula, setMassFormula] = useState('');
    const [applyToAllBrands, setApplyToAllBrands] = useState(false);
    const [massDependentMetrics, setMassDependentMetrics] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [filteredBrands, setFilteredBrands] = useState([]);

    const loadAllMetrics = useCallback(async () => {
        console.log('Tüm günlük ve aylık metrikler yükleniyor...');
        setLoadingAllMetrics(true);
        try {
            const [dailyResponse, monthlyResponse] = await Promise.all([
                api.get('/metrics', { params: { scope: 'all' } }),
                api.get('/monthly-metrics', { params: { minimal: 'true' } })
            ]);

            const dailyMetrics = dailyResponse.data
                ?.filter(metric => metric && !metric.isDeleted)
                .map(metric => ({
                    id: parseInt(metric.id),
                    name: metric.name,
                    type: 'daily'
                })) || [];
            setAllDailyMetrics(dailyMetrics);
            console.log(`${dailyMetrics.length} adet aktif günlük metrik yüklendi.`);

            const monthlyMetrics = monthlyResponse.data
                ?.filter(metric => metric && !metric.isDeleted && (!id || metric.id.toString() !== id.toString()))
                .map(metric => ({
                    id: parseInt(metric.id),
                    name: metric.name,
                    type: 'monthly'
                })) || [];
            setAllMonthlyMetrics(monthlyMetrics);
            console.log(`${monthlyMetrics.length} adet aktif aylık metrik (düzenlenen hariç) yüklendi.`);

        } catch (error) {
            console.error('Tüm metrikler yüklenirken hata:', error);
            message.error('Bağımlı metrik listesi yüklenirken bir hata oluştu.');
            setAllDailyMetrics([]);
            setAllMonthlyMetrics([]);
        } finally {
            setLoadingAllMetrics(false);
        }
    }, [id]);

    const loadBrands = useCallback(async () => {
        try {
            const response = await api.get('/brands');
            if (!response.data || !Array.isArray(response.data)) {
                throw new Error('Geçersiz API yanıtı');
            }
            const sortedBrands = response.data
                .filter(brand => brand && brand.name)
                .sort((a, b) => a.name.localeCompare(b.name));
            setBrands(sortedBrands);
            setFilteredBrands(sortedBrands);
            console.log(`${sortedBrands.length} adet marka yüklendi.`);

        } catch (error) {
            console.error('Markalar yüklenirken hata:', error);
            message.error('Markalar yüklenirken bir hata oluştu');
        }
    }, []);

    useEffect(() => {
        if (!selectedTemplate) {
            setFilteredBrands(brands);
        } else {
            const filtered = brands.filter(brand => brand.template === selectedTemplate);
            setFilteredBrands(filtered);
        }
    }, [selectedTemplate, brands]);

    const loadMonthlyMetric = useCallback(async () => {
        if (!id) return;

        console.log(`Aylık metrik ${id} yükleniyor...`);
        setLoading(true);

        try {
            const response = await api.get(`/monthly-metrics/${id}`);
            const metric = response.data;

            if (!metric) {
                throw new Error('Metrik bulunamadı');
            }
            console.log('Metrik verisi alındı:', metric);

            const formattedColor = metric.color?.startsWith('#')
                ? metric.color
                : (metric.color ? `#${metric.color}` : '#FFFFFF');

            form.setFieldsValue({
                name: metric.name,
                description: metric.description,
                internalNotes: metric.internalNotes || '',
                dataType: metric.dataType || 'number',
                isAutoCalculated: metric.isAutoCalculated,
                color: formattedColor,
                brands: metric.Brands?.map(brand => {
                    const dependentMetrics = brand.BrandMonthlyMetric.dependentMetrics || [];
                    const parsedDependentMetrics = Array.isArray(dependentMetrics)
                        ? dependentMetrics
                        : (typeof dependentMetrics === 'string'
                            ? JSON.parse(dependentMetrics)
                            : []);

                    const normalizedDependentMetrics = parsedDependentMetrics
                        .map(m => typeof m === 'string' ? parseInt(m, 10) : m)
                        .filter(m => !isNaN(m));

                    return {
                        brandId: brand.id,
                        formula: brand.BrandMonthlyMetric.formula || '',
                        dependentMetrics: normalizedDependentMetrics
                    };
                }) || []
            });
            console.log('Form alanları dolduruldu.');

        } catch (error) {
            console.error('Metrik yüklenirken hata:', error);
            message.error('Metrik yüklenirken bir hata oluştu: ' + (error.response?.data?.error || error.message));
            navigate('/monthly-metrics');
        } finally {
            setLoading(false);
        }
    }, [id, form, navigate]);

    useEffect(() => {
        let mounted = true;
        setLoadingInitialData(true);

        const loadInitialData = async () => {
            try {
                await Promise.all([
                    loadBrands(),
                    loadAllMetrics()
                ]);

                if (id && mounted) {
                    await loadMonthlyMetric();
                }
            } catch (error) {
                console.error('İlk veri yükleme sırasında genel hata:', error);
            } finally {
                if (mounted) {
                    setLoadingInitialData(false);
                }
            }
        };

        loadInitialData();

        return () => {
            mounted = false;
        };
    }, [id, loadBrands, loadAllMetrics]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            console.log('Form gönderiliyor:', values);

            if (!values.name?.trim()) {
                message.error('Metrik adı boş olamaz');
                setLoading(false);
                return;
            }

            if (!selectedTemplate && !applyToAllBrands && (!values.brands || values.brands.length === 0)) {
                message.warning('Hiçbir marka seçilmedi. En az bir marka seçin, template seçin veya "Tüm Markalara Otomatik Ekle" seçeneğini açın.');
                setLoading(false);
                return;
            }

            if (values.isAutoCalculated && applyToAll) {
                if (!massFormula?.trim()) {
                    message.warning('Otomatik hesaplanan metrikler için toplu formül girilmelidir.');
                    setLoading(false);
                    return;
                }
            }

            if (values.isAutoCalculated && !applyToAll && !applyToAllBrands && values.brands) {
                for (const brandData of values.brands) {
                    if (!brandData.formula?.trim()) {
                        const brandName = brands.find(b => b.id === brandData.brandId)?.name || `Marka ${brandData.brandId}`;
                        message.warning(`${brandName} için formül girilmelidir.`);
                        setLoading(false);
                        return;
                    }
                    if (!brandData.dependentMetrics || brandData.dependentMetrics.length === 0) {
                        const brandName = brands.find(b => b.id === brandData.brandId)?.name || `Marka ${brandData.brandId}`;
                        message.warning(`${brandName} için en az bir bağımlı metrik seçilmelidir.`);
                        setLoading(false);
                        return;
                    }
                }
            }

            const color = values.color && typeof values.color === 'string'
                ? (values.color.startsWith('#') ? values.color : `#${values.color}`)
                : '#FFFFFF';

            const formData = {
                name: values.name,
                description: values.description,
                internalNotes: values.internalNotes,
                dataType: values.dataType,
                isAutoCalculated: values.isAutoCalculated,
                color,
                applyToAllBrands,
                selectedTemplate,
                applyToAll,
                massFormula: applyToAll ? massFormula : undefined,
                massDependentMetrics: applyToAll ? massDependentMetrics : undefined,
                brands: (!applyToAllBrands && !selectedTemplate) ? values.brands?.map(b => ({
                    brandId: b.brandId,
                    formula: !applyToAll ? b.formula : undefined,
                    dependentMetrics: !applyToAll ? b.dependentMetrics : undefined
                })) : undefined
            };

            console.log('Hazırlanan form verisi:', formData);

            message.loading({
                content: `Metrik ${id ? 'güncelleniyor' : 'oluşturuluyor'}. Bu işlem biraz zaman alabilir...`,
                key: 'metricOperation',
                duration: 0
            });

            if (selectedTemplate || applyToAllBrands) {
                let infoText = "";
                let brandCount = 0;
                if (selectedTemplate) {
                    brandCount = brands.filter(brand => brand.template === selectedTemplate).length;
                    infoText = `Tüm ${selectedTemplate} template'ine sahip ${brandCount} markaya uygulama yapılıyor.`;
                } else if (applyToAllBrands) {
                    brandCount = brands.length;
                    infoText = `Tüm ${brandCount} markaya uygulama yapılıyor.`;
                }
                if (brandCount > 0) {
                    message.info({
                        content: `${infoText} Bu işlem biraz uzun sürebilir, lütfen bekleyin.`,
                        key: 'metricOperationInfo',
                        duration: 5
                    });
                }
            }

            let response;
            if (id) {
                response = await api.put(`/monthly-metrics/${id}`, formData);
                message.success({
                    content: `Aylık metrik başarıyla güncellendi.`,
                    key: 'metricOperation',
                    duration: 2
                });
            } else {
                response = await api.post('/monthly-metrics', formData);
                message.success({
                    content: `Aylık metrik başarıyla oluşturuldu.`,
                    key: 'metricOperation',
                    duration: 2
                });
            }

            if (values.isAutoCalculated) {
                message.info('Metrik değerleri arka planda hesaplanmaya başlandı. Bu işlem biraz zaman alabilir.');
            }

            if (response.status === 200 || response.status === 201) {
                setTimeout(() => navigate('/monthly-metrics'), 500);
            }
        } catch (error) {
            console.error('Form gönderme hatası:', error);
            const errorMsg = error.response?.data?.error || error.message || 'Bilinmeyen bir hata oluştu.';
            const detailedError = error.response?.data?.details || errorMsg;

            message.error({
                content: 'İşlem sırasında bir hata oluştu: ' + detailedError,
                key: 'metricOperation',
                duration: 5
            });
        } finally {
            setLoading(false);
        }
    };

    const handleMassFormulaChange = (formula) => {
        setMassFormula(formula);
    };

    const handleMassDependentMetricsChange = (selectedMetrics) => {
        setMassDependentMetrics(selectedMetrics);
    };

    const handleTemplateChange = (value) => {
        setSelectedTemplate(value);
    };

    const combinedMetrics = [...allDailyMetrics, ...allMonthlyMetrics].sort((a, b) => {
        if (a.type !== b.type) {
            return a.type === 'daily' ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
    });

    if (loadingInitialData) {
        return <div style={{ padding: 24, textAlign: 'center' }}><Spin size="large" tip="Veriler yükleniyor..." /></div>;
    }

    return (
        <div style={{ padding: 24 }}>
            <Card
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{id ? 'Aylık Metrik Düzenle' : 'Yeni Aylık Metrik Ekle'}</span>
                        <Button
                            type="default"
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setLoadingInitialData(true);
                                Promise.all([loadBrands(), loadAllMetrics()])
                                    .then(() => {
                                        if (id) return loadMonthlyMetric();
                                        else form.resetFields({ brands: [] });
                                    })
                                    .catch(err => console.error("Yenileme hatası:", err))
                                    .finally(() => setLoadingInitialData(false));
                                message.success('Form yenileniyor...');
                            }}
                            loading={loading || loadingInitialData || loadingAllMetrics}
                        >
                            {id ? 'Yenile' : 'Sıfırla'}
                        </Button>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        dataType: 'number',
                        isAutoCalculated: false,
                        color: '#FFFFFF',
                        internalNotes: '',
                        brands: []
                    }}
                >
                    <Form.Item
                        name="name"
                        label="Metrik Adı"
                        rules={[{ required: true, message: 'Lütfen metrik adı girin' }]}
                    >
                        <Input placeholder="Örnek: Ziyaretçi Başına Ciro" maxLength={100} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Açıklama"
                    >
                        <TextArea
                            placeholder="Metrik hakkında açıklama (Türkçe)"
                            rows={3}
                            maxLength={500}
                        />
                    </Form.Item>

                    <Form.Item
                        name="internalNotes"
                        label="Dahili Notlar"
                        tooltip="Bu notlar tooltip'te görünür"
                    >
                        <TextArea
                            placeholder="Dahili notlar "
                            rows={3}
                            maxLength={1000}
                        />
                    </Form.Item>

                    <Form.Item
                        name="dataType"
                        label="Veri Tipi"
                        rules={[{ required: true, message: 'Lütfen veri tipi seçin' }]}
                    >
                        <Select placeholder="Veri tipi seçin">
                            <Option value="number">Sayı</Option>
                            <Option value="currency">Para Birimi (₺)</Option>
                            <Option value="percentage">Yüzde (%)</Option>
                            <Option value="text">Metin</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="color"
                        label="Renk (Grafikler için)"
                    >
                        <Input type="color" style={{ width: '50px', height: '32px', padding: '0' }} />
                    </Form.Item>

                    <Form.Item
                        name="isAutoCalculated"
                        label="Otomatik Hesaplama"
                        valuePropName="checked"
                        tooltip="İşaretlenirse, metrik değeri formül ile otomatik hesaplanır. İşaretlenmezse, manuel veri girişi yapılır."
                    >
                        <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
                    </Form.Item>

                    <Card title="Marka Uygulama Seçenekleri" style={{ marginBottom: '20px' }} size="small">
                        <Form.Item label="Template Filtresi (Opsiyonel)">
                            <Select
                                placeholder="Belirli bir template'e sahip markalara uygula"
                                style={{ width: '100%' }}
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                allowClear
                            >
                                <Option value="WEB AUTOBUDGET">WEB AUTOBUDGET</Option>
                                <Option value="WEB MANUELBUDGET">WEB MANUELBUDGET</Option>
                                <Option value="TRENDYOL">TRENDYOL</Option>
                            </Select>
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
                                Template seçerseniz, aşağıdaki "Tüm Markalara Otomatik Ekle" ve "Markaları Manuel Seç" alanları devre dışı kalır ve işlem sadece seçili template'e sahip markalara uygulanır.
                            </div>
                        </Form.Item>

                        <Form.Item
                            label="Tüm Markalara Otomatik Ekle"
                            tooltip="İşaretlenirse, bu metrik tüm aktif markalara otomatik olarak eklenir/güncellenir. Aşağıdaki 'Markaları Manuel Seç' alanı devre dışı kalır."
                            >
                            <Switch
                                checked={applyToAllBrands}
                                onChange={(checked) => setApplyToAllBrands(checked)}
                                disabled={!!selectedTemplate}
                                checkedChildren="Evet"
                                unCheckedChildren="Hayır"
                            />
                        </Form.Item>
                    </Card>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.isAutoCalculated !== currentValues.isAutoCalculated
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('isAutoCalculated') ? (
                                <Card title="Toplu Formül ve Bağımlı Metrikler (Otomatik Hesaplama için)" style={{ marginBottom: '20px' }} size="small">
                                    <Form.Item
                                        label="Tüm Uygulanacak Markalara Aynı Formülü ve Bağımlıları Kullan"
                                        tooltip="İşaretlenirse, aşağıdaki formül ve bağımlı metrikler seçilen template'e, tüm markalara veya manuel seçilen markalara topluca uygulanır."
                                        >
                                        <Switch
                                            checked={applyToAll}
                                            onChange={(checked) => setApplyToAll(checked)}
                                            checkedChildren="Evet"
                                            unCheckedChildren="Hayır"
                                        />
                                    </Form.Item>

                                    {applyToAll && (
                                        <Form.Item
                                            label={
                                                <span>
                                                    Toplu Formül
                                                    <Tooltip overlayInnerStyle={{ width: '800px', fontSize: '12px', maxWidth: '800px' }} title={formulaHelpText}>
                                                        <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            rules={[{ required: applyToAll, message: 'Toplu uygulama için formül gereklidir' }]}
                                        >
                                            <Input.TextArea
                                                value={massFormula}
                                                onChange={(e) => handleMassFormulaChange(e.target.value)}
                                                placeholder="Tüm uygulanacak markalara atanacak formül"
                                                rows={3}
                                            />
                                        </Form.Item>
                                    )}

                                    {applyToAll && (
                                        <Form.Item
                                            label="Toplu Bağımlı Metrikler"
                                            rules={[{ required: applyToAll, message: 'Toplu uygulama için en az bir bağımlı metrik seçin' }]}
                                        >
                                            <Select
                                                mode="multiple"
                                                placeholder="Formülde kullanılacak metrikleri seçin"
                                                style={{ width: '100%' }}
                                                value={massDependentMetrics}
                                                onChange={handleMassDependentMetricsChange}
                                                loading={loadingAllMetrics}
                                                optionFilterProp="label"
                                                showSearch={true}
                                                filterOption={(input, option) =>
                                                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                }
                                                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                notFoundContent={loadingAllMetrics ? <Spin size="small" /> : "Metrik bulunamadı"}
                                            >
                                                {combinedMetrics.map(metric => (
                                                    <Select.Option
                                                        key={`${metric.type}_${metric.id}`}
                                                        value={metric.id}
                                                        label={`${metric.name} (${metric.type === 'daily' ? 'Günlük' : 'Aylık'})`}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{metric.name}</span>
                                                            <span style={{
                                                                color: metric.type === 'daily' ? '#52c41a' : '#1890ff',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {metric.type === 'daily' ? 'Günlük' : 'Aylık'}
                                                            </span>
                                                        </div>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                     )}
                                </Card>
                            ) : null
                        }
                    </Form.Item>

                    {!applyToAllBrands && !selectedTemplate && (
                        <Card title="Markaları Manuel Seç" style={{ marginBottom: '20px' }}>
                             <div style={{ marginBottom: '12px', fontSize: '12px', color: '#888' }}>
                                Bu metriğin uygulanacağı markaları buradan tek tek seçebilirsiniz. Otomatik hesaplama açıksa, her marka için farklı formül ve bağımlı metrik tanımlayabilirsiniz (eğer yukarıdaki "Toplu Uygula" seçeneği kapalıysa).
                            </div>
                            <Form.List name="brands">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <Card
                                                key={field.key}
                                                size="small"
                                                title={`Marka ${index + 1}`}
                                                style={{ marginBottom: 16 }}
                                                extra={
                                                    <Button danger onClick={() => remove(field.name)}>
                                                        Kaldır
                                                    </Button>
                                                }
                                            >
                                                <Form.Item
                                                    {...field}
                                                    label="Marka"
                                                    name={[field.name, 'brandId']}
                                                    rules={[{ required: true, message: 'Lütfen marka seçin' }]}
                                                    fieldKey={[field.fieldKey, 'brandId']}
                                                >
                                                    <Select
                                                        showSearch
                                                        optionFilterProp="children"
                                                        filterOption={(input, option) =>
                                                            (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        placeholder="Marka seçin"
                                                    >
                                                        {brands.map(brand => (
                                                            <Option key={brand.id} value={brand.id}>
                                                                {brand.name} {brand.template ? `(${brand.template})` : ''}
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>

                                                <Form.Item
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) =>
                                                         prevValues.isAutoCalculated !== currentValues.isAutoCalculated || prevValues.applyToAll !== currentValues.applyToAll
                                                    }
                                                >
                                                    {(formProps) => {
                                                        const isAutoCalculated = formProps.getFieldValue('isAutoCalculated');
                                                        const isMassApplyEnabled = formProps.getFieldValue('applyToAll');

                                                        if (!isAutoCalculated || isMassApplyEnabled) return null;

                                                        return (
                                                            <>
                                                                <Form.Item
                                                                    {...field}
                                                                    label={
                                                                        <span>
                                                                            Formül{' '}
                                                                            <Tooltip overlayInnerStyle={{ width: '800px', fontSize: '12px', maxWidth: '800px' }} title={formulaHelpText}>
                                                                                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                                                                            </Tooltip>
                                                                        </span>
                                                                    }
                                                                    name={[field.name, 'formula']}
                                                                    rules={[{ required: true, message: 'Lütfen formül girin' }]}
                                                                    fieldKey={[field.fieldKey, 'formula']}
                                                                >
                                                                    <Input.TextArea placeholder="Örn: sum(Ciro) / sum(Ziyaretci)" rows={2} />
                                                                </Form.Item>

                                                                <Form.Item
                                                                    {...field}
                                                                    label="Bağımlı Metrikler"
                                                                    name={[field.name, 'dependentMetrics']}
                                                                    fieldKey={[field.fieldKey, 'dependentMetrics']}
                                                                    rules={[
                                                                        { required: true, message: 'En az bir bağımlı metrik seçin' },
                                                                    ]}
                                                                >
                                                                    <Select
                                                                        mode="multiple"
                                                                        placeholder="Formülde kullanılacak metrikleri seçin"
                                                                        loading={loadingAllMetrics}
                                                                        optionFilterProp="label"
                                                                        showSearch={true}
                                                                        filterOption={(input, option) =>
                                                                            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                                                        }
                                                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                                                        style={{ width: '100%' }}
                                                                        notFoundContent={loadingAllMetrics ? <Spin size="small" /> : "Metrik bulunamadı"}
                                                                    >
                                                                        {combinedMetrics.map(metric => (
                                                                            <Select.Option
                                                                                key={`${metric.type}_${metric.id}`}
                                                                                value={metric.id}
                                                                                label={`${metric.name} (${metric.type === 'daily' ? 'Günlük' : 'Aylık'})`}
                                                                            >
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                                    <span>{metric.name}</span>
                                                                                    <span style={{
                                                                                        color: metric.type === 'daily' ? '#52c41a' : '#1890ff',
                                                                                        fontWeight: 'bold'
                                                                                    }}>
                                                                                        {metric.type === 'daily' ? 'Günlük' : 'Aylık'}
                                                                                    </span>
                                                                                </div>
                                                                            </Select.Option>
                                                                        ))}
                                                                    </Select>
                                                                </Form.Item>
                                                            </>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </Card>
                                        ))}

                                        <Button
                                            type="dashed"
                                            onClick={() => add()}
                                            block
                                            icon={<PlusOutlined />}
                                            disabled={applyToAllBrands || !!selectedTemplate}
                                        >
                                            Marka Manuel Ekle
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        </Card>
                    )}

                    <Form.Item style={{ marginTop: 24 }}>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={loading || loadingInitialData}>
                                {id ? 'Metriği Güncelle' : 'Metriği Oluştur'}
                            </Button>
                            <Button onClick={() => navigate('/monthly-metrics')} disabled={loading || loadingInitialData}>
                                İptal
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default MonthlyMetricForm; 